import { Children, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { Add, AddToInsights } from '@sas/react-svg';
import cx from 'classnames';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useRefArray } from '../../hooks/useRefArray';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { getOSName, OSName } from '../../util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { Flow } from '../Layouts/Flow';
import { Scrollable } from '../Scrollable';
import { Tile, TileDragPreview, TileDragType, TilesContextProvider, useTileDroppable } from '../Tile';
import { ZeroState } from '../ZeroState';
import { PageIndicator } from './__internal__';
import { useTileCarouselKeyboardNav } from './__internal__/hooks';
import bundle from './NovaComponents-TileCarousel-gui-icu.properties';
import './TileCarousel.css';
import cn from './TileCarousel.css.json';
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('TileCarousel');
export const TileCarousel = /*#__PURE__*/forwardRef(function TileCarousel({
  pageIndicatorLocation = 'below',
  maxNumTiles = 100,
  ...props
}, ref) {
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    'spacing-s': dt.tokens['space-base-s'],
    'spacing-m': dt.tokens['space-base-m'],
    'add-button-bd': dt.tokens['color-container-border-4']
  }, 'tile-carousel');
  const directSpeech = useDirectSpeech();
  const [scrollIndex, setScrollIndex] = useState(props.initialFocusableIndex ?? 0);
  const scrollableContentRef = useRef(null);
  const allowDeselection = props.selectionMode === 'single' ? props.allowDeselection ?? true : true;

  // This is updated to the height of an actual tile.  We can then use this to size
  // the ZeroStateMessage so the whole carousel keeps the same height if all tiles are
  // removed.
  const [zeroStateHeight, setZeroStateHeight] = useState(0);
  const [scrollWindowWidth, setScrollWindowWidth] = useState(0);
  const [tileWidth, setTileWidth] = useState(0);
  const tilesPerPage = tileWidth ? Math.floor(scrollWindowWidth / tileWidth) : 0;
  const selectionAnchorIndexRef = useRef(0);
  const selectionMode = props.selectionMode ?? 'multi';
  const hideCheckbox = selectionMode === 'none' || selectionMode === 'single' || selectionMode === 'desktop';
  const getContinuousSelectionIndices = index => {
    const startIndex = Math.min(selectionAnchorIndexRef.current, index);
    const endIndex = Math.max(selectionAnchorIndexRef.current, index);
    return [...Array(endIndex - startIndex + 1)].map((_, i) => startIndex + i);
  };
  const isMacOS = getOSName() === OSName.Mac;
  const onSelect = (index, e) => {
    if (selectionMode === 'none' || !props.tiles) return;
    const continuousSelectionIndices = e.shiftKey ? getContinuousSelectionIndices(index) : undefined;
    const isCtrlOrCmdPressed = isMacOS ? e.metaKey : e.ctlKey;
    const shouldDeselectOtherTiles = selectionMode === 'single' || e.selectionArea === 'tile' && !isCtrlOrCmdPressed;
    const toDeselectIndices = shouldDeselectOtherTiles ? props.tiles.reduce((prev, tile, i) => tile.selected &&
    // Do not deselect the tiles in the continuous selection range
    !continuousSelectionIndices?.includes(i) &&
    // Do not deselect the current tile
    i !== index ? [...prev, i] : prev, []) : [];
    if (continuousSelectionIndices) {
      //  For continuous tile selection select all the tile in the current range if it is not selected
      for (const tileIndex of continuousSelectionIndices) {
        const tile = props.tiles[tileIndex];
        if (!tile.selected) {
          tile.onSelect?.({
            index: tileIndex,
            selectionArea: 'checkbox'
          });
        }
      }
    } else {
      // For single tile selection, keep the current tile selected if deselection is not allowed
      // or other tiles are deselected by this selection action
      const shouldKeepSelected = props.tiles[index].selected && (!allowDeselection || toDeselectIndices.length > 0);
      if (!shouldKeepSelected) {
        if (selectionMode === 'single' && !disableInteractiveMode && props.tiles[index].selected) {
          directSpeech?.notify(localize('unselected.aria'));
        }
        props.tiles[index].onSelect?.(e);
      }
      // Update the selection anchor to be the current tile
      selectionAnchorIndexRef.current = index;
    }
    for (const tileIndex of toDeselectIndices) {
      props.tiles[tileIndex].onSelect?.({
        index: tileIndex,
        selectionArea: 'checkbox'
      });
    }
  };
  const {
    refs: tileRefs,
    createSetRef: createTileRef
  } = useRefArray();
  const numberOfTiles = Math.min(maxNumTiles, (props.tiles ?? Children.toArray(props.children)).length);
  const {
    focusableIndex,
    setFocusableIndex,
    handleKeyDown
  } = useTileCarouselKeyboardNav({
    tileRefs,
    tilesPerPage,
    numberOfTiles,
    scrollableContentRef,
    initialFocusableIndex: props.initialFocusableIndex,
    onSelect,
    reorderTiles: props.reorderTiles,
    tiles: props.tiles
  });
  const {
    wrapDndProps
  } = useDndEarcon({
    numberOfTiles,
    maxNumTiles
  });
  const tileNodes = props.tiles?.slice(0, maxNumTiles).map((tileProps, index) => /*#__PURE__*/_createElement(Tile, {
    ...tileProps,
    key: index,
    onSelect: e => onSelect(index, e),
    tabIndex: index === focusableIndex ? 0 : -1,
    index: tileProps.index ?? index,
    dndProps: wrapDndProps(tileProps.dndProps)
  })) ?? Children.toArray(props.children).slice(0, maxNumTiles);
  const numberOfPages = tilesPerPage ? Math.ceil(numberOfTiles / tilesPerPage) : 0;
  // Assume each page indicator dot takes 18px
  const hasSpaceForPageIndicator = scrollWindowWidth > numberOfPages * 18;
  const showPageIndicator = props.showPageIndicator && hasSpaceForPageIndicator && numberOfPages > 1;
  const showAddButton = !!props.onAdd;
  const addButtonTooltip = props.addButtonProps?.tooltip ?? localize('addTile.button.tooltip.label');
  const onAddTile = async () => {
    const {
      status,
      message
    } = (await props.onAdd?.()) ?? {};
    if (message) {
      directSpeech?.notify(message);
    } else if (status === 'success') {
      directSpeech?.notify(localize('tileAdded.aria'));
    }
  };
  const [setScrollWindowRef, getScrollWindow] = useResizeObserverRef(() => {
    const scrollWindow = getScrollWindow();
    setZeroStateHeight(scrollWindow?.offsetHeight ?? 0);
    const scrollWindowWidth = scrollWindow?.clientWidth ?? 0;
    if (scrollWindowWidth <= 0) return;

    // Assume all the tiles have the same width, only measure the width of the first tile
    setTileWidth(getTileWidth(tileRefs?.[0]));
    setScrollWindowWidth(scrollWindowWidth);
  });
  useEffect(() => {
    if (numberOfTiles > maxNumTiles) {
      logger.debug(`TileCarousel is limited to ${maxNumTiles} tiles`);
    }
  }, [numberOfTiles, maxNumTiles]);
  useEffect(() => {
    // When the last tile is focusable and removed, set focus on the new last tile
    setFocusableIndex(prev => Math.min(numberOfTiles - 1, prev));
    // When the last tile is removed, scroll to the new last tile
    setScrollIndex(prev => Math.min(numberOfTiles - 1, prev));
    // When the last tile is anchor and removed, set the new last tile as anchor
    selectionAnchorIndexRef.current = Math.min(numberOfTiles - 1, selectionAnchorIndexRef.current);
  }, [numberOfTiles, setFocusableIndex]);
  useImperativeHandle(ref, () => ({
    scrollTo: ({
      tileIndex,
      pageIndex
    }) => {
      if (tileIndex !== undefined) {
        setScrollIndex(tileIndex);
      } else if (pageIndex !== undefined) {
        setScrollIndex(pageIndex * tilesPerPage);
      }
    },
    setFocusableTile: setFocusableIndex
  }));
  const getTileContainerProps = index => ({
    onFocus: () => {
      setFocusableIndex(index);
      setScrollIndex(index);
    }
  });
  const pageIndicator = showPageIndicator ? /*#__PURE__*/_jsx(PageIndicator, {
    totalPages: numberOfPages,
    currentPageIndex: Math.floor(scrollIndex / tilesPerPage),
    onPageTo: pageIndex => {
      setScrollIndex(pageIndex * tilesPerPage);
    },
    ariaLabel: props.pageIndicatorAriaLabel,
    tooltip: props.pageIndicatorTooltip
  }) : null;
  const scrollableCurrentIndex = showPageIndicator ? Math.floor(scrollIndex / tilesPerPage) : scrollIndex;
  useEffect(() => {
    if (!showPageIndicator) return;
    const onScrollPositionChange = props.onScrollPositionChange;
    const firstTileOnPageIndex = scrollableCurrentIndex * tilesPerPage;
    const lastTileOnPageIndex = Math.min(numberOfTiles - 1, firstTileOnPageIndex + tilesPerPage - 1);
    onScrollPositionChange?.({
      tileIndices: [...Array(lastTileOnPageIndex - firstTileOnPageIndex + 1)].map((_, i) => firstTileOnPageIndex + i),
      pageIndex: scrollableCurrentIndex,
      numberOfPages
    });
    // If the current focusable tile is not visible on the current page,
    // update the focusable tile to be the first tile on the page after scrolling
    setFocusableIndex(prevFocusableIndex => {
      return prevFocusableIndex >= firstTileOnPageIndex && prevFocusableIndex <= lastTileOnPageIndex ? prevFocusableIndex : firstTileOnPageIndex;
    });
  }, [scrollableCurrentIndex, showPageIndicator, tilesPerPage, numberOfPages, numberOfTiles, props.onScrollPositionChange, setFocusableIndex]);
  const addButtonIconType = props.addButtonProps?.iconType ?? 'add';

  // disableInteractiveMode should be ignored in multi selection mode because there is a checkbox
  const disableInteractiveMode = selectionMode === 'multi' ? false : props.disableInteractiveMode;
  const {
    ariaLabel
  } = getCarouselA11yInfo(localize, {
    ...props,
    disableInteractiveMode
  });

  // To check if a tile is dragged over the container so that the Add button can be shown for dropping the tile to the end
  const {
    ref: containerDropRef,
    isOver: isOverContainer
  } = useTileDroppable({
    allowDrop: props.dndProps?.allowDrop,
    allowDropDndPrefixes: props.dndProps?.allowDropDndPrefixes,
    dndPrefix: props.dndProps?.dndPrefix
  });
  const droppableProps = useTileDroppable({
    ...wrapDndProps(props.dndProps),
    dropMode: 'combine'
  });
  const {
    ref: droppableRef = undefined,
    dropClassName: droppableClassName = undefined,
    style: droppableStyle = undefined
  } = props.dndProps ? droppableProps : {};
  return /*#__PURE__*/_jsx("div", {
    style: {
      ...tokens,
      ...props.style
    },
    className: cx(cn.carouselContainer, props.className),
    "data-testid": props.testId,
    ref: containerDropRef,
    children: numberOfTiles > 0 ? /*#__PURE__*/_jsxs(Flow, {
      orientation: "horizontal",
      spacing: "m",
      className: cn.innerContainer,
      children: [/*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        spacing: "xs",
        className: cn.scrollableContainer,
        children: [pageIndicatorLocation === 'above' ? pageIndicator : null, /*#__PURE__*/_jsx("div", {
          onKeyDown: handleKeyDown,
          children: /*#__PURE__*/_jsxs(TilesContextProvider, {
            value: {
              createTileRef,
              hideCheckbox,
              isSelected: () => false,
              disableInteractiveMode,
              selectionMode,
              role: disableInteractiveMode ? 'option' : undefined
            },
            children: [/*#__PURE__*/_jsx(Scrollable
            // Because page indicator is not considered as part of Scrollable, set selectedIndex the same as currentIndex
            // so that the scrolling happens when scrollIndex is updated via page indicator
            , {
              selectedIndex: scrollableCurrentIndex,
              currentIndex: scrollableCurrentIndex,
              setCurrentIndex: setCurrentIndex => {
                if (typeof setCurrentIndex === 'function') {
                  setScrollIndex(prev => showPageIndicator ? setCurrentIndex(prev) * tilesPerPage : setCurrentIndex(prev));
                } else {
                  const index = setCurrentIndex;
                  setScrollIndex(showPageIndicator ? index * tilesPerPage : index);
                }
              },
              acceptDropTypes: [TileDragType],
              contentProps: {
                'aria-label': ariaLabel,
                role: disableInteractiveMode ? 'listbox' : 'group',
                'aria-multiselectable': disableInteractiveMode && selectionMode === 'desktop' ? 'true' : undefined
              },
              scrollWindowRef: setScrollWindowRef,
              scrollWindowClassName: cn.scrollWindow,
              contentRef: scrollableContentRef,
              variant: props.variant,
              testId: postfixTestId(props.testId, 'scrollable'),
              children: showPageIndicator ? groupTilesByPage(tileNodes, tilesPerPage, tileWidth, maxNumTiles).map((tilePage, pageIndex) => /*#__PURE__*/_jsx(Flow, {
                spacing: "none",
                // Leave some room to prevent Scrollable from shaking when re-rendered
                style: scrollWindowWidth ? {
                  width: `${scrollWindowWidth - 8}px`
                } : undefined,
                className: cn.pageContainer,
                children: tilePage.map((tile, currPageTileIndex) => {
                  const tileIndex = pageIndex * tilesPerPage + currPageTileIndex;
                  return /*#__PURE__*/_jsx("div", {
                    ...getTileContainerProps(tileIndex),
                    children: tile
                  }, currPageTileIndex);
                })
              }, pageIndex)) : tileNodes.map((tile, index) => /*#__PURE__*/_jsx("div", {
                ...getTileContainerProps(index),
                children: tile
              }, index))
            }), /*#__PURE__*/_jsx(TileDragPreview, {})]
          })
        }), pageIndicatorLocation === 'below' ? pageIndicator : null]
      }), showAddButton || props.dndProps ? /*#__PURE__*/_jsx("div", {
        ref: droppableRef,
        className: cx(droppableClassName,
        // Hide it through css instead of using React conditional rendering
        // so that `ref` can be set up correctly before it is shown
        !showAddButton && !isOverContainer && cn.hidden),
        style: droppableStyle,
        children: /*#__PURE__*/_jsx(Button, {
          tooltip: addButtonTooltip,
          ariaLabel: addButtonTooltip,
          ...props.addButtonProps,
          testId: postfixTestId(props.testId, 'addTileButton'),
          icon: {
            icon: addButtonIconType === 'addToInsights' ? AddToInsights : Add
          },
          variant: ButtonVariant.Icon,
          disabled: numberOfTiles >= maxNumTiles,
          className: cn.addButton,
          onPress: onAddTile
        })
      }) : null]
    }) : /*#__PURE__*/_jsx("div", {
      ref: droppableRef,
      className: cx(droppableClassName, cn.zeroStateContainer),
      style: droppableStyle,
      children: /*#__PURE__*/_jsx(ZeroState, {
        testId: postfixTestId(props.testId, 'zeroState'),
        text: props.zeroStateMsg ?? (props.useAddZeroStateMsg ? localize('carousel.zeroState.noItemsAdded.label') : localize('carousel.zeroState.noItems.label')),
        style: {
          height: `${zeroStateHeight}px`
        },
        className: cn.zeroState,
        actionButtonOneProps: showAddButton ? {
          tooltip: addButtonTooltip,
          ariaLabel: addButtonTooltip,
          ...props.addButtonProps,
          text: localize('addTile.zeroState.button.label'),
          testId: postfixTestId(props.testId, 'zeroState-addButton'),
          onPress: onAddTile
        } : undefined
      })
    })
  });
});
const getCarouselA11yInfo = (localize, {
  selectionMode = 'multi',
  ...props
}) => {
  const carouselAriaLabel = props.ariaLabel ?? localize('carousel.aria');
  const ariaLabels = [carouselAriaLabel];
  if (selectionMode === 'single') {
    ariaLabels.push(localize('selectionMode.single.aria'));
  } else if (selectionMode === 'multi' || selectionMode === 'desktop') {
    ariaLabels.push(localize('selectionMode.multi.aria'));
  }
  if (!props.disableInteractiveMode) {
    ariaLabels.push(localize('interactiveMode.hint.aria'));
  }

  // TODO: modify if all tiles are object tiles or all are presentation tiles and
  // include selection mode info.  This is not implemented yet.

  return {
    ariaLabel: ariaLabels.join(', ')
  };
};
const DEFAULT_TILE_WIDTH = 160;
const getTileWidth = tileContainer => {
  if (!tileContainer) return 0;
  const {
    marginLeft,
    marginRight,
    paddingLeft,
    paddingRight
  } = window.getComputedStyle(tileContainer);
  const marginX = parseFloat(marginLeft) + parseFloat(marginRight);
  const paddingX = parseFloat(paddingLeft) + parseFloat(paddingRight);
  return (tileContainer?.offsetWidth ?? DEFAULT_TILE_WIDTH) + marginX + paddingX;
};
const groupTilesByPage = (tiles, tilesPerPage, tileWidth, maxNumTiles) => {
  const numberOfTiles = tiles.length;
  const tilePages = [];
  for (const [index, child] of tiles.entries()) {
    if (index >= maxNumTiles) break;
    if (index % tilesPerPage === 0) {
      // Create a new tile page
      tilePages.push([child]);
    } else {
      // Add this tile to the previous tile page
      tilePages[tilePages.length - 1].push(child);
    }
    if (index === numberOfTiles - 1) {
      const numberOfTilesInLastPage = tilePages[tilePages.length - 1].length;
      // Fill the last page with empty tile placeholder
      tilePages[tilePages.length - 1].push(...new Array(tilesPerPage - numberOfTilesInLastPage).fill( /*#__PURE__*/_jsx("div", {
        style: {
          width: tileWidth
        }
      })));
    }
  }
  return tilePages;
};
const useDndEarcon = ({
  numberOfTiles,
  maxNumTiles
}) => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const wrapDndProps = dndProps => {
    return dndProps ? {
      ...dndProps,
      onDrop: e => {
        if (numberOfTiles >= maxNumTiles) {
          // When the TileCarousel already has the maximum number of tiles, play earcon if another tile is dropped
          earcon.play();
        } else {
          dndProps?.onDrop?.(e);
        }
      }
    } : undefined;
  };
  return {
    wrapDndProps
  };
};
//# sourceMappingURL=TileCarousel.js.map